// "Ääntämisen apu äänityksissä" on this device (engine/src/coach/rec-prefs.ts; Axel's requirement of 2026-10-12):
// which of the coach's features run in my own recordings. Each is optional per person and per feature, independent
// of Ääntämisvalmentaja (UserInfo.coach), all off by default; with every one off nothing here changes anything.
// Light on purpose (no coach code): RecordCompare, RecordPrompt and the saved-take rows read it for everyone, and
// load the features' own code (coach/rec/*, coach chunks) only when one is on. Never in the demo.
import type { UserInfo } from '@valyrio/engine/api';
import { anyRecCoach, effectiveRecCoach, readRecCoachPrefs, REC_COACH_OFF, recCoachUsesModel, type RecCoachPrefs } from '@valyrio/engine/coach/rec-prefs';
import { useApp } from '../../state/store';

/** The stored choices as they are (the settings show these, with what each still needs). */
export const recCoachChosen = (me: Pick<UserInfo, 'recCoach'> | null | undefined): RecCoachPrefs => readRecCoachPrefs(me?.recCoach ?? null);

/** What actually runs for a person: a feature and everything it needs on (dependencies enforced). */
export const recCoachFor = (me: Pick<UserInfo, 'recCoach'> | null | undefined): RecCoachPrefs => effectiveRecCoach(recCoachChosen(me));

// one frozen object per stored value, so a selector returns the same object while nothing changed
let last: { src: unknown; chosen: RecCoachPrefs; on: RecCoachPrefs } = { src: undefined, chosen: REC_COACH_OFF, on: REC_COACH_OFF };
function cached(src: UserInfo['recCoach'] | undefined): typeof last {
  if (src === last.src) return last;
  const chosen = recCoachChosen(src ? { recCoach: src } : {});
  last = { src, chosen, on: effectiveRecCoach(chosen) };
  return last;
}

/** The features in effect for me (all off in the demo). */
export function useRecCoach(): RecCoachPrefs {
  const demo = useApp((s) => s.identity === 'demo');
  const on = useApp((s) => cached(s.me.recCoach).on);
  return demo ? REC_COACH_OFF : on;
}

/** My choices as stored (the settings). */
export function useRecCoachChosen(): RecCoachPrefs {
  return useApp((s) => cached(s.me.recCoach).chosen);
}

/** Any feature in effect for me. */
export function useRecCoachAny(): boolean {
  return anyRecCoach(useRecCoach());
}

/** The phone model is needed for me (Äännevihje on): the download row and the warm-up. */
export const recCoachNeedsModel = (me: Pick<UserInfo, 'recCoach'> | null | undefined): boolean => recCoachUsesModel(recCoachChosen(me));

/** The partner lets their takes be asked about (MeResponse.partner.recCoachEar); never in the demo. */
export function usePartnerEar(): boolean {
  return useApp((s) => s.identity !== 'demo' && s.partner?.recCoachEar === true);
}
